import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Subscription } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import { ChatExportEligibility, ChatExportTask } from '../models/chat-export.model';
import { ChatExportService } from '../services/chat-export.service';

@Component({
  selector: 'servicenumber-chat-export-form',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzInputModule, NzIconModule, I18nPipe, TestIdDirective],
  templateUrl: './chat-export-form.component.html',
  styleUrls: ['./chat-export-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatExportFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly chatExportService = inject(ChatExportService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private langSubscription?: Subscription;

  @Input() serviceNumberId: string = '';
  @Input() timeZone: string = 'Asia/Taipei';
  @Input() maxMonth: string = '';
  @Input() canManage: boolean = true;
  @Input() records: ChatExportTask[] = [];
  @Input() submitting: boolean = false;

  @Output() readonly submitExport = new EventEmitter<string>();
  @Output() readonly viewRecords = new EventEmitter<void>();
  @Output() readonly previewStyle = new EventEmitter<void>();

  selectedMonth: string = '';
  rangeText: string = '';
  eligibility: ChatExportEligibility = ChatExportEligibility.CAN_EXPORT;

  ngOnInit(): void {
    if (!this.selectedMonth && this.maxMonth) {
      this.selectedMonth = this.maxMonth;
    }
    this.updateComputedState();

    if (this.i18n?.change) {
      this.langSubscription = this.i18n.change.subscribe(() => {
        this.updateComputedState();
        this.cdr.markForCheck();
      });
    }
  }

  ngOnChanges(): void {
    if (!this.selectedMonth && this.maxMonth) {
      this.selectedMonth = this.maxMonth;
    }
    this.updateComputedState();
  }

  ngOnDestroy(): void {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
      this.langSubscription = undefined;
    }
  }

  onMonthChange(newMonth: string): void {
    this.selectedMonth = newMonth;
    this.updateComputedState();
  }

  updateComputedState(): void {
    this.rangeText = this.chatExportService.getMonthRangeText(this.selectedMonth);
    this.eligibility = this.chatExportService.checkEligibility(this.selectedMonth, this.maxMonth, this.records, this.canManage);
  }

  get canSubmit(): boolean {
    return this.eligibility === ChatExportEligibility.CAN_EXPORT && !this.submitting;
  }

  get showExistingButton(): boolean {
    return (
      this.eligibility === ChatExportEligibility.HAS_READY ||
      this.eligibility === ChatExportEligibility.IS_PROCESSING ||
      this.eligibility === ChatExportEligibility.IS_DELETING
    );
  }

  get eligibilityBannerClass(): string {
    switch (this.eligibility) {
      case ChatExportEligibility.CAN_EXPORT:
        return 'note-banner good';
      case ChatExportEligibility.NO_PERMISSION:
      case ChatExportEligibility.INVALID_MONTH:
        return 'note-banner error';
      default:
        return 'note-banner info';
    }
  }

  get eligibilityBannerKey(): string {
    switch (this.eligibility) {
      case ChatExportEligibility.CAN_EXPORT:
        return 'servicenumber.chat-export.eligibility.can-export';
      case ChatExportEligibility.HAS_READY:
        return 'servicenumber.chat-export.eligibility.has-ready';
      case ChatExportEligibility.IS_PROCESSING:
        return 'servicenumber.chat-export.eligibility.is-processing';
      case ChatExportEligibility.IS_DELETING:
        return 'servicenumber.chat-export.eligibility.is-deleting';
      case ChatExportEligibility.NO_PERMISSION:
        return 'servicenumber.chat-export.eligibility.no-permission';
      case ChatExportEligibility.HAS_OTHER_PROCESSING:
        return 'servicenumber.chat-export.eligibility.has-other-processing';
      case ChatExportEligibility.INVALID_MONTH:
      default:
        return 'servicenumber.chat-export.eligibility.invalid-month';
    }
  }

  get eligibilityBannerText(): string {
    if (this.i18n) {
      return this.i18n.fanyi(this.eligibilityBannerKey);
    }
    switch (this.eligibility) {
      case ChatExportEligibility.CAN_EXPORT:
        return '可建立整月匯出；不設訊息數或 Room 數上限。';
      case ChatExportEligibility.HAS_READY:
        return '本月份已有可用檔案，不再提供重複匯出。';
      case ChatExportEligibility.IS_PROCESSING:
        return '本月份正在處理，請查看既有記錄。';
      case ChatExportEligibility.IS_DELETING:
        return '本月檔案正在移除，完成前不能重新匯出。';
      case ChatExportEligibility.NO_PERMISSION:
        return '目前不是本號管理者，無法建立匯出。';
      case ChatExportEligibility.HAS_OTHER_PROCESSING:
        return '本號已有處理中任務，請稍後再建立。';
      case ChatExportEligibility.INVALID_MONTH:
      default:
        return '只能選擇已結束月份，不可選本月或未來月份。';
    }
  }

  onSubmit(): void {
    if (this.canSubmit) {
      this.submitExport.emit(this.selectedMonth);
    }
  }

  onViewRecords(): void {
    this.viewRecords.emit();
  }

  onPreviewStyle(): void {
    this.previewStyle.emit();
  }
}
